import type { ReactNode } from 'react'
import type { components } from '../../../api/generated/schema'
import { Modal, SectionLabel } from '../../../design/components'
import { formatRefresh } from '../types'
import styles from './sections.module.css'

type Schema = components['schemas']
type Shape = Schema['ShapeDto']

/**
 * Everything the sheet knows about one ability.
 *
 * Deliberately one type covering both an ability a character has taken
 * (`AbilityDto`) and one still in the catalog (`AbilityReferenceDto`): they are
 * the same subject read at two moments, and the fields they share are the ones
 * worth reading. What only the taken one carries — its shape assignments and the
 * stress and consequence slots it granted — is optional here rather than a second
 * component.
 */
export type AbilityLike = Schema['AbilityDto'] | Schema['AbilityReferenceDto']

/**
 * The full record of an ability, as a dialog.
 *
 * A dialog rather than an expander in the row because this is reference material —
 * you open it, read it, and dismiss it without changing anything, which is the
 * one job {@link Modal} exists for. The card rows stay short enough to scan; the
 * detail that would bury them lives one tap away.
 */
export function AbilityDetailsModal({
  ability,
  shapes = [],
  action,
  onClose,
}: {
  ability: AbilityLike
  /** Every shape the character has, for reading `shapeIds` back as names. */
  shapes?: Shape[]
  /** An action for the dialog's foot — adding it, when the ability is still a template. */
  action?: ReactNode
  onClose: () => void
}) {
  return (
    <Modal title={ability.name ?? 'Ability'} onClose={onClose}>
      <AbilityDetails ability={ability} shapes={shapes} />
      {action !== undefined && <div className={styles.actions}>{action}</div>}
    </Modal>
  )
}

export function AbilityDetails({
  ability,
  shapes = [],
}: {
  ability: AbilityLike
  shapes?: Shape[]
}) {
  const spellcasting = ability.spellcastingType
  const casts = spellcasting !== undefined && spellcasting !== 'None'
  const specialization = ability.specializationScore ?? 0
  const magicItems = ability.magicItemScore ?? 0

  // Only present on an ability a character has actually taken.
  const taken = 'extraStress' in ability ? ability : undefined
  const extraStress = taken?.extraStress ?? []
  const extraConsequences = taken?.extraConsequences ?? []
  const shapeIds = taken?.shapeIds ?? []

  const description = ability.description ?? ''

  return (
    <div className={styles.detailList}>
      <Detail term="Type">{ability.type ?? 'Ability'}</Detail>
      <Detail term="Refresh cost">{formatRefresh(ability.refreshCost ?? 0)}</Detail>

      <Detail term="Description">
        {description === '' ? (
          <span className={styles.empty}>No description.</span>
        ) : (
          <span className={styles.detailProse}>{description}</span>
        )}
      </Detail>

      {casts && <Detail term="Spellcasting">{String(spellcasting)}</Detail>}

      {/* Access level is meaningless without a discipline behind it — the add form
          zeroes it in the same case. */}
      {casts && ability.spellAccessLevel !== undefined && ability.spellAccessLevel !== 'None' && (
        <Detail term="Spell access">{String(ability.spellAccessLevel)}</Detail>
      )}

      {specialization > 0 && <Detail term="Specialization points">{specialization}</Detail>}
      {magicItems > 0 && <Detail term="Magic-item points">{magicItems}</Detail>}

      {extraStress.length > 0 && (
        <Detail term="Extra stress">
          {extraStress
            .map((extra) => `+${extra.value ?? 0} ${String(extra.type ?? '')}`)
            .join(' · ')}
        </Detail>
      )}

      {extraConsequences.length > 0 && (
        <Detail term="Extra consequences">
          {extraConsequences
            .map((extra) => `${String(extra.level ?? '')} ${String(extra.type ?? '')}`)
            .join(' · ')}
        </Detail>
      )}

      {ability.isVampirism === true && (
        <Detail term="Vampirism">
          A vampirism type — the kind of creature, not a power on top
        </Detail>
      )}

      {ability.grantsHungerTrack === true && (
        <Detail term="Hunger">Gives the character their Hunger track</Detail>
      )}

      {ability.isVampirismRelated === true && (
        <Detail term="Feeds Hunger">Its refresh cost counts against the Hunger track</Detail>
      )}

      {/* An ability with no shapes applies in all of them — the same rule the card filters by. */}
      {shapes.length > 1 && (
        <Detail term="Applies in">
          {shapeIds.length === 0
            ? 'All shapes'
            : shapes
                .filter((shape) => shapeIds.includes(shape.id ?? 0))
                .map((shape) => shape.name ?? '')
                .join(' · ')}
        </Detail>
      )}
    </div>
  )
}

function Detail({ term, children }: { term: string; children: ReactNode }) {
  return (
    <div className={styles.detailRow}>
      <SectionLabel tone="muted" micro>
        {term}
      </SectionLabel>
      <div className={styles.detailValue}>{children}</div>
    </div>
  )
}
